/**
 * TeachingTool — unified page for 5 teaching AI tools.
 * URL param `:tool` selects which form/renderer to use.
 *   /teaching/lesson-planner    → lesson-plan
 *   /teaching/question-paper    → question-paper
 *   /teaching/worksheet          → worksheet
 *   /teaching/flashcards         → flashcards
 *   /teaching/mind-map           → mind-map
 */
import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  GraduationCap, ClipboardList, FileText, LayoutGrid, Network,
  Sparkles, Download, AlertCircle, Loader2, ArrowLeft, Copy, Check,
  PenTool, Library, FileQuestion, Target, Clock, Brain, Mail, Layers,
  Volume2, Play,
} from "lucide-react";
import PremiumNav from "@/components/premium/PremiumNav";
import PremiumFooter from "@/components/premium/PremiumFooter";
import { PremiumCard, PremiumButton, SectionHeading, Pill } from "@/components/premium/Primitives";

const API = process.env.REACT_APP_BACKEND_URL || '';

const TOOL_CONFIG = {
  "lesson-planner": {
    endpoint: "/api/teaching/lesson-plan",
    Icon: GraduationCap,
    eyebrow: "Teaching Suite",
    title: "AI Lesson Planner",
    subtitle: "Topic + grade → 45-min structured lesson plan with objectives, activities, differentiation and homework.",
    fields: [
      { name: "topic", label: "Topic", type: "text", placeholder: "e.g. Photosynthesis", required: true },
      { name: "grade", label: "Grade", type: "select", options: ["K","1","2","3","4","5","6","7","8","9","10","11","12"], default: "8" },
      { name: "subject", label: "Subject", type: "text", placeholder: "e.g. Biology" },
      { name: "duration_min", label: "Duration (min)", type: "number", default: 45, min: 10, max: 180 },
      { name: "learning_style", label: "Learning style", type: "select", options: ["balanced","visual","auditory","kinesthetic","reading-writing"], default: "balanced" },
    ],
  },
  "question-paper": {
    endpoint: "/api/teaching/question-paper",
    Icon: ClipboardList,
    eyebrow: "Teaching Suite",
    title: "AI Question Paper",
    subtitle: "Generate a balanced MCQ + short + long-answer paper with full answer key in seconds.",
    fields: [
      { name: "topic", label: "Topic", type: "text", placeholder: "e.g. Newton's Laws of Motion", required: true },
      { name: "grade", label: "Grade", type: "select", options: ["K","1","2","3","4","5","6","7","8","9","10","11","12"], default: "8" },
      { name: "subject", label: "Subject", type: "text", placeholder: "e.g. Physics" },
      { name: "mcq_count", label: "MCQs", type: "number", default: 10, min: 0, max: 50 },
      { name: "short_count", label: "Short answers", type: "number", default: 5, min: 0, max: 20 },
      { name: "long_count", label: "Long answers", type: "number", default: 2, min: 0, max: 10 },
      { name: "difficulty", label: "Difficulty", type: "select", options: ["easy","medium","hard","mixed"], default: "medium" },
    ],
  },
  worksheet: {
    endpoint: "/api/teaching/worksheet",
    Icon: FileText,
    eyebrow: "Teaching Suite",
    title: "Worksheet Generator",
    subtitle: "Print-ready, grade-appropriate worksheets with progressive difficulty and a complete answer key.",
    fields: [
      { name: "topic", label: "Topic", type: "text", placeholder: "e.g. Fractions", required: true },
      { name: "grade", label: "Grade", type: "select", options: ["K","1","2","3","4","5","6","7","8","9","10","11","12"], default: "5" },
      { name: "subject", label: "Subject", type: "text", placeholder: "e.g. Mathematics" },
      { name: "sections", label: "Sections", type: "number", default: 4, min: 2, max: 8 },
      { name: "include_answer_key", label: "Include answer key", type: "boolean", default: true },
    ],
  },
  flashcards: {
    endpoint: "/api/teaching/flashcards",
    Icon: LayoutGrid,
    eyebrow: "Teaching Suite",
    title: "Flashcard Generator",
    subtitle: "Spaced-repetition flashcards with concise fronts, comprehensive backs and optional memory hints.",
    fields: [
      { name: "topic", label: "Topic", type: "text", placeholder: "e.g. Periodic Table elements", required: true },
      { name: "count", label: "Cards", type: "number", default: 20, min: 5, max: 50 },
      { name: "grade", label: "Grade (optional)", type: "select", options: ["","K","1","2","3","4","5","6","7","8","9","10","11","12"], default: "" },
      { name: "difficulty", label: "Difficulty", type: "select", options: ["easy","medium","hard"], default: "medium" },
    ],
  },
  "mind-map": {
    endpoint: "/api/teaching/mind-map",
    Icon: Network,
    eyebrow: "Teaching Suite",
    title: "Mind Map Generator",
    subtitle: "Hierarchical, balanced mind maps with 3-8 branches and configurable depth.",
    fields: [
      { name: "topic", label: "Topic", type: "text", placeholder: "e.g. The Human Body", required: true },
      { name: "branches", label: "Root branches", type: "number", default: 5, min: 3, max: 8 },
      { name: "depth", label: "Depth", type: "number", default: 2, min: 1, max: 3 },
    ],
  },
  "teaching-notes": {
    endpoint: "/api/teaching/teaching-notes",
    Icon: PenTool,
    eyebrow: "Teaching Suite",
    title: "Teaching Notes",
    subtitle: "Chapter-ready notes with key concepts, examples, misconceptions and discussion questions.",
    fields: [
      { name: "topic", label: "Topic", type: "text", placeholder: "e.g. The Mughal Empire", required: true },
      { name: "grade", label: "Grade", type: "select", options: ["K","1","2","3","4","5","6","7","8","9","10","11","12"], default: "8" },
      { name: "subject", label: "Subject", type: "text", placeholder: "e.g. History" },
    ],
  },
  "study-material": {
    endpoint: "/api/teaching/study-material",
    Icon: Library,
    eyebrow: "Teaching Suite",
    title: "Study Material",
    subtitle: "Student-friendly handout with overview, sections, key terms and self-check questions.",
    fields: [
      { name: "topic", label: "Topic", type: "text", placeholder: "e.g. Photosynthesis", required: true },
      { name: "grade", label: "Grade", type: "select", options: ["K","1","2","3","4","5","6","7","8","9","10","11","12"], default: "6" },
      { name: "subject", label: "Subject", type: "text", placeholder: "e.g. Biology" },
    ],
  },
  "question-bank": {
    endpoint: "/api/teaching/question-bank",
    Icon: FileQuestion,
    eyebrow: "Teaching Suite",
    title: "Question Bank",
    subtitle: "30-100 diverse questions balanced across MCQ, short and long with difficulty labels.",
    fields: [
      { name: "topic", label: "Topic", type: "text", placeholder: "e.g. Algebra basics", required: true },
      { name: "grade", label: "Grade", type: "select", options: ["K","1","2","3","4","5","6","7","8","9","10","11","12"], default: "8" },
      { name: "subject", label: "Subject", type: "text", placeholder: "e.g. Math" },
      { name: "count", label: "Total questions", type: "number", default: 30, min: 10, max: 100 },
    ],
  },
  rubric: {
    endpoint: "/api/teaching/rubric",
    Icon: Target,
    eyebrow: "Teaching Suite",
    title: "Rubric Generator",
    subtitle: "Assignment → 4-level evaluation rubric with weighted criteria and scoring guide.",
    fields: [
      { name: "assignment", label: "Assignment description", type: "text", placeholder: "e.g. Persuasive essay on climate change", required: true },
      { name: "grade", label: "Grade", type: "select", options: ["K","1","2","3","4","5","6","7","8","9","10","11","12"], default: "8" },
      { name: "criteria_count", label: "Criteria", type: "number", default: 5, min: 3, max: 8 },
    ],
  },
  homework: {
    endpoint: "/api/teaching/homework",
    Icon: Clock,
    eyebrow: "Teaching Suite",
    title: "Homework Generator",
    subtitle: "Multi-day homework schedule with varied tasks, parent notes and weekly goal.",
    fields: [
      { name: "topic", label: "Topic", type: "text", placeholder: "e.g. Solar system", required: true },
      { name: "grade", label: "Grade", type: "select", options: ["K","1","2","3","4","5","6","7","8","9","10","11","12"], default: "5" },
      { name: "subject", label: "Subject", type: "text", placeholder: "e.g. Science" },
      { name: "days", label: "Number of days", type: "number", default: 5, min: 1, max: 15 },
      { name: "minutes_per_day", label: "Minutes / day", type: "number", default: 30, min: 10, max: 120 },
    ],
  },
  assignment: {
    endpoint: "/api/teaching/assignment",
    Icon: Brain,
    eyebrow: "Teaching Suite",
    title: "Assignment Generator",
    subtitle: "Project-based assignment with deliverables, milestones, resources and evaluation criteria.",
    fields: [
      { name: "topic", label: "Topic", type: "text", placeholder: "e.g. Renewable energy in India", required: true },
      { name: "grade", label: "Grade", type: "select", options: ["K","1","2","3","4","5","6","7","8","9","10","11","12"], default: "8" },
      { name: "subject", label: "Subject", type: "text", placeholder: "e.g. Environmental Science" },
      { name: "type", label: "Type", type: "select", options: ["project","research","creative","case_study"], default: "project" },
      { name: "duration_days", label: "Duration (days)", type: "number", default: 7, min: 1, max: 60 },
    ],
  },
  "parent-communication": {
    endpoint: "/api/teaching/parent-communication",
    Icon: Mail,
    eyebrow: "Teaching Suite",
    title: "Parent Communication",
    subtitle: "Warm, culturally-aware parent messages — email, WhatsApp, PTA notes, PT-meeting scripts. With Hindi translation baked in.",
    fields: [
      { name: "student_name", label: "Student name", type: "text", placeholder: "e.g. Aarav Sharma", required: true },
      { name: "grade", label: "Grade", type: "select", options: ["K","1","2","3","4","5","6","7","8","9","10","11","12"], default: "8" },
      { name: "situation", label: "What are you writing about?", type: "textarea", placeholder: "e.g. Aarav has missed homework 3 times this week. Kind but firm reminder.", required: true },
      { name: "channel", label: "Channel", type: "select", options: ["email","whatsapp","in-app","printed-note"], default: "email" },
      { name: "tone", label: "Tone", type: "select", options: ["warm-professional","firm-supportive","celebratory","urgent"], default: "warm-professional" },
      { name: "teacher_name", label: "Signed by (optional)", type: "text", placeholder: "e.g. Priya Ma'am" },
    ],
  },
  "course-outline": {
    endpoint: "/api/teaching/course-outline",
    Icon: Layers,
    eyebrow: "Teaching Suite",
    title: "Course Outline",
    subtitle: "Multi-week course planner — modules, milestones, activities, resources, final assessment. Ready to hand to a substitute.",
    fields: [
      { name: "course_title", label: "Course title", type: "text", placeholder: "e.g. Introduction to Python for Grade 8", required: true },
      { name: "grade", label: "Grade", type: "select", options: ["K","1","2","3","4","5","6","7","8","9","10","11","12"], default: "8" },
      { name: "subject", label: "Subject", type: "text", placeholder: "e.g. Computer Science" },
      { name: "total_weeks", label: "Total weeks", type: "number", default: 8, min: 2, max: 52 },
      { name: "hours_per_week", label: "Hours / week", type: "number", default: 3, min: 1, max: 20 },
      { name: "learning_goals", label: "Learning goals (optional)", type: "textarea", placeholder: "e.g. Understand variables, loops, functions; build a small project" },
      { name: "audience_notes", label: "Audience notes (optional)", type: "text", placeholder: "e.g. Mixed-ability class, weekend batch, CBSE X board prep" },
    ],
  },
};

function FieldInput({ field, value, onChange }) {
  const base = "w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-[#A855F7] focus:ring-4 focus:ring-brand-100 outline-none text-sm font-sans bg-white";
  if (field.type === "select") {
    return (
      <select value={value ?? field.default ?? ""} onChange={(e) => onChange(e.target.value)} className={base} data-testid={`field-${field.name}`}>
        {field.options.map((o) => <option key={o} value={o}>{o || "—"}</option>)}
      </select>
    );
  }
  if (field.type === "textarea") {
    return (
      <textarea
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder}
        rows={4}
        className={base + " resize-y min-h-[100px]"}
        data-testid={`field-${field.name}`}
      />
    );
  }
  if (field.type === "number") {
    return (
      <input
        type="number"
        value={value ?? field.default ?? ""}
        min={field.min} max={field.max}
        onChange={(e) => onChange(Number(e.target.value))}
        className={base}
        data-testid={`field-${field.name}`}
      />
    );
  }
  if (field.type === "boolean") {
    return (
      <label className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 cursor-pointer hover:border-[#A855F7] transition-colors">
        <input type="checkbox" checked={!!(value ?? field.default)} onChange={(e) => onChange(e.target.checked)} className="accent-[#A855F7]" data-testid={`field-${field.name}`}/>
        <span className="text-sm text-slate-600">{field.label}</span>
      </label>
    );
  }
  return (
    <input
      type="text"
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={field.placeholder}
      className={base}
      data-testid={`field-${field.name}`}
    />
  );
}

// ─── Result renderers ─────────────────────────────────────────────────
function LessonPlanResult({ data }) {
  return (
    <div className="space-y-6">
      <PremiumCard className="p-6" hover={false}>
        <h2 className="font-heading text-2xl font-bold text-[#111111]">{data.title}</h2>
        <div className="flex flex-wrap gap-2 mt-3">
          <Pill variant="brand">Grade {data.grade}</Pill>
          <Pill variant="outline">{data.duration_min} min</Pill>
          {data.subject && <Pill variant="neutral">{data.subject}</Pill>}
        </div>
      </PremiumCard>

      <PremiumCard className="p-6" hover={false}>
        <h3 className="font-heading text-lg font-semibold text-[#111111] mb-3">Learning objectives</h3>
        <ul className="space-y-2">
          {(data.learning_objectives || []).map((o, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-slate-700"><Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0"/>{o}</li>
          ))}
        </ul>
      </PremiumCard>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <PremiumCard className="p-6" hover={false}>
          <h3 className="font-heading text-base font-semibold text-[#111111] mb-3">Materials</h3>
          <ul className="space-y-1.5 text-sm text-slate-600">
            {(data.materials || []).map((m, i) => <li key={i}>· {m}</li>)}
          </ul>
        </PremiumCard>
        <PremiumCard className="p-6" hover={false}>
          <h3 className="font-heading text-base font-semibold text-[#111111] mb-3">Prerequisites</h3>
          <ul className="space-y-1.5 text-sm text-slate-600">
            {(data.prerequisites || []).map((m, i) => <li key={i}>· {m}</li>)}
          </ul>
        </PremiumCard>
      </div>

      <PremiumCard className="p-6" hover={false}>
        <h3 className="font-heading text-lg font-semibold text-[#111111] mb-4">Lesson structure</h3>
        <div className="space-y-3">
          {(data.lesson_structure || []).map((p, i) => (
            <div key={i} className="p-4 rounded-xl border border-slate-200 bg-slate-50/40">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h4 className="font-semibold text-[#111111] text-sm">{i + 1}. {p.phase}</h4>
                <Pill variant="brand">{p.duration_min} min</Pill>
              </div>
              <p className="text-sm text-slate-700 mt-2"><strong>Activity:</strong> {p.activity}</p>
              <p className="text-xs text-slate-500 mt-1"><strong>Teacher:</strong> {p.teacher_action}</p>
              <p className="text-xs text-slate-500 mt-1"><strong>Students:</strong> {p.student_action}</p>
            </div>
          ))}
        </div>
      </PremiumCard>

      {data.differentiation && (
        <PremiumCard className="p-6" hover={false}>
          <h3 className="font-heading text-lg font-semibold text-[#111111] mb-3">Differentiation</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm text-slate-700">
            <div className="p-3 rounded-lg bg-slate-50"><strong>Struggling:</strong> {data.differentiation.struggling}</div>
            <div className="p-3 rounded-lg bg-slate-50"><strong>Advanced:</strong> {data.differentiation.advanced}</div>
            <div className="p-3 rounded-lg bg-slate-50"><strong>ELL:</strong> {data.differentiation.ell}</div>
          </div>
        </PremiumCard>
      )}

      <PremiumCard className="p-6 bg-brand-50/40 border-brand-200" hover={false}>
        <h3 className="font-heading text-base font-semibold text-[#111111] mb-2">Homework</h3>
        <p className="text-sm text-slate-700">{data.homework}</p>
      </PremiumCard>
    </div>
  );
}

function QuestionPaperResult({ data }) {
  return (
    <div className="space-y-6">
      <PremiumCard className="p-6" hover={false}>
        <h2 className="font-heading text-2xl font-bold text-[#111111]">{data.title}</h2>
        <div className="flex flex-wrap gap-2 mt-3">
          <Pill variant="brand">Grade {data.grade}</Pill>
          {data.subject && <Pill variant="outline">{data.subject}</Pill>}
          <Pill variant="neutral">Duration: {data.duration_min} min</Pill>
          <Pill variant="dark">Total marks: {data.total_marks}</Pill>
        </div>
        {data.instructions && (
          <ol className="mt-4 text-sm text-slate-600 list-decimal list-inside space-y-1">
            {data.instructions.map((ins, i) => <li key={i}>{ins}</li>)}
          </ol>
        )}
      </PremiumCard>

      {(data.mcq || []).length > 0 && (
        <PremiumCard className="p-6" hover={false}>
          <h3 className="font-heading text-lg font-semibold text-[#111111] mb-4">Section A — Multiple choice ({data.mcq.length} × 1 mark)</h3>
          <div className="space-y-4">
            {data.mcq.map((q, i) => (
              <div key={i} className="p-4 rounded-xl border border-slate-200">
                <p className="text-sm font-semibold text-[#111111]">Q{i + 1}. {q.q}</p>
                <ul className="mt-2 space-y-1 text-sm text-slate-700">
                  {q.options.map((o, j) => <li key={j}>{o}</li>)}
                </ul>
                <p className="mt-3 text-xs text-emerald-700 font-semibold">Answer: {q.answer}</p>
                {q.explanation && <p className="mt-1 text-xs text-slate-500">{q.explanation}</p>}
              </div>
            ))}
          </div>
        </PremiumCard>
      )}

      {(data.short || []).length > 0 && (
        <PremiumCard className="p-6" hover={false}>
          <h3 className="font-heading text-lg font-semibold text-[#111111] mb-4">Section B — Short answers</h3>
          <div className="space-y-3">
            {data.short.map((q, i) => (
              <div key={i} className="p-4 rounded-xl border border-slate-200">
                <div className="flex items-start justify-between flex-wrap gap-2">
                  <p className="text-sm font-semibold text-[#111111] flex-1">Q{i + 1}. {q.q}</p>
                  <Pill variant="brand">{q.marks} marks</Pill>
                </div>
                <p className="mt-2 text-xs text-emerald-700"><strong>Model answer:</strong> {q.answer}</p>
              </div>
            ))}
          </div>
        </PremiumCard>
      )}

      {(data.long || []).length > 0 && (
        <PremiumCard className="p-6" hover={false}>
          <h3 className="font-heading text-lg font-semibold text-[#111111] mb-4">Section C — Long answers</h3>
          <div className="space-y-3">
            {data.long.map((q, i) => (
              <div key={i} className="p-4 rounded-xl border border-slate-200">
                <div className="flex items-start justify-between flex-wrap gap-2">
                  <p className="text-sm font-semibold text-[#111111] flex-1">Q{i + 1}. {q.q}</p>
                  <Pill variant="brand">{q.marks} marks</Pill>
                </div>
                <p className="mt-2 text-xs text-emerald-700"><strong>Model answer:</strong> {q.answer}</p>
                {q.key_points && (
                  <div className="mt-2">
                    <p className="text-xs text-slate-500 font-semibold">Key points:</p>
                    <ul className="text-xs text-slate-600 mt-1 space-y-0.5">
                      {q.key_points.map((p, j) => <li key={j}>· {p}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        </PremiumCard>
      )}
    </div>
  );
}

function WorksheetResult({ data }) {
  return (
    <div className="space-y-6">
      <PremiumCard className="p-6" hover={false}>
        <h2 className="font-heading text-2xl font-bold text-[#111111]">{data.title}</h2>
        <div className="flex flex-wrap gap-2 mt-3">
          <Pill variant="brand">Grade {data.grade}</Pill>
          {data.subject && <Pill variant="outline">{data.subject}</Pill>}
        </div>
        <p className="mt-4 text-sm text-slate-600">{data.instructions}</p>
      </PremiumCard>

      {(data.sections || []).map((sec, i) => (
        <PremiumCard key={i} className="p-6" hover={false}>
          <h3 className="font-heading text-lg font-semibold text-[#111111] mb-2">{sec.name}</h3>
          <p className="text-sm text-slate-600 mb-4">{sec.instructions}</p>
          <div className="space-y-3">
            {(sec.exercises || []).map((ex, j) => (
              <div key={j} className="p-3 rounded-lg border border-slate-200 bg-slate-50/40">
                {ex.type === "match" ? (
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {(ex.pairs || []).map((p, k) => (
                      <div key={k} className="contents"><div className="p-2 bg-white rounded border border-slate-200">{p.left}</div><div className="p-2 bg-white rounded border border-slate-200">{p.right}</div></div>
                    ))}
                  </div>
                ) : (
                  <>
                    <p className="text-sm font-medium text-[#111111]">{ex.question}</p>
                    {ex.answer && <p className="mt-1 text-xs text-emerald-700"><strong>Answer:</strong> {ex.answer}</p>}
                  </>
                )}
              </div>
            ))}
          </div>
        </PremiumCard>
      ))}
    </div>
  );
}

function FlashcardsResult({ data }) {
  const [flipped, setFlipped] = useState({});
  return (
    <div className="space-y-4">
      <PremiumCard className="p-6" hover={false}>
        <h2 className="font-heading text-2xl font-bold text-[#111111]">{data.topic}</h2>
        <div className="flex flex-wrap gap-2 mt-3">
          <Pill variant="brand">{data.count} cards</Pill>
          <Pill variant="outline">{data.difficulty}</Pill>
        </div>
        <p className="mt-3 text-sm text-slate-500">Click any card to flip.</p>
      </PremiumCard>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {(data.cards || []).map((c, i) => (
          <button
            key={i}
            onClick={() => setFlipped((f) => ({ ...f, [i]: !f[i] }))}
            data-testid={`flashcard-${i}`}
            className="text-left"
          >
            <PremiumCard className="p-5 h-40 flex flex-col justify-between" interactive>
              <div>
                <Pill variant="neutral">{c.category || "General"}</Pill>
                <p className="mt-3 text-sm font-semibold text-[#111111]">
                  {flipped[i] ? c.back : c.front}
                </p>
              </div>
              <div className="text-xs text-slate-400">{flipped[i] ? "← back · click to flip" : "click to reveal →"}</div>
            </PremiumCard>
          </button>
        ))}
      </div>
    </div>
  );
}

function MindMapResult({ data }) {
  const colorMap = { purple: "bg-brand-50 text-[#7E22CE] border-brand-200", slate: "bg-slate-100 text-slate-700 border-slate-200", black: "bg-[#111111] text-white border-[#111111]", brand: "bg-[#A855F7] text-white border-[#A855F7]" };
  return (
    <div className="space-y-6">
      <PremiumCard className="p-6 text-center" hover={false}>
        <Pill variant="brand">Root</Pill>
        <h2 className="mt-3 font-heading text-3xl font-bold text-[#111111]">{data.root}</h2>
        <p className="mt-3 text-sm text-slate-600 max-w-xl mx-auto">{data.summary}</p>
      </PremiumCard>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {(data.branches || []).map((b, i) => (
          <PremiumCard key={i} className="p-5" hover={false}>
            <div className={`inline-block px-3 py-1 rounded-lg text-sm font-semibold border ${colorMap[b.color] || colorMap.purple}`}>
              {b.title}
            </div>
            <ul className="mt-4 space-y-2">
              {(b.children || []).map((c, j) => (
                <li key={j} className="text-sm">
                  <div className="font-medium text-[#111111]">· {c.title}</div>
                  {c.children && (
                    <ul className="ml-4 mt-1 text-xs text-slate-500 space-y-0.5">
                      {c.children.map((l, k) => <li key={k}>– {l.title}</li>)}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </PremiumCard>
        ))}
      </div>
    </div>
  );
}

const RENDERERS = {
  "lesson-planner": LessonPlanResult,
  "question-paper": QuestionPaperResult,
  worksheet: WorksheetResult,
  flashcards: FlashcardsResult,
  "mind-map": MindMapResult,
  "teaching-notes": DocumentResult,
  "study-material": DocumentResult,
  "question-bank": QuestionBankResult,
  rubric: RubricResult,
  homework: HomeworkResult,
  assignment: AssignmentResult,
  "parent-communication": ParentCommsResult,
  "course-outline": CourseOutlineResult,
};

// ─── Parent Communication renderer ────────────────────────────────────
function ParentCommsResult({ data }) {
  const [copied, setCopied] = useState("");
  const [voiceUrl, setVoiceUrl] = useState("");
  const [voiceLang, setVoiceLang] = useState("hi-IN");
  const [voiceSpeaker, setVoiceSpeaker] = useState("shreya");
  const [voiceLoading, setVoiceLoading] = useState(false);
  const doCopy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(""), 1500);
  };

  const generateVoiceNote = async () => {
    // Prefer Hindi translation if available; else the whatsapp version; else the full body.
    const text = (voiceLang === "hi-IN" && data.hindi_translation)
      ? data.hindi_translation
      : (data.whatsapp_short || `${data.opening || ""}\n${data.body || ""}\n${data.call_to_action || ""}`.trim());
    if (!text) return;
    setVoiceLoading(true); setVoiceUrl("");
    try {
      const res = await axios.post(`${API}/api/sarvam/tts`,
        { text, language_code: voiceLang, speaker: voiceSpeaker, pace: 0.95 },
        { responseType: "blob" }
      );
      setVoiceUrl(URL.createObjectURL(res.data));
    } catch (e) {
      // eslint-disable-next-line no-alert
      window?.alert?.("Voice-note generation failed. Please try a shorter message.");
    }
    finally { setVoiceLoading(false); }
  };
  const fullMessage = [
    data.opening,
    "",
    data.body,
    "",
    data.call_to_action,
    "",
    data.closing,
  ].filter(Boolean).join("\n");
  return (
    <div className="space-y-6" data-testid="parent-comms-result">
      <PremiumCard className="p-6" hover={false}>
        <h2 className="font-heading text-2xl font-bold text-[#111111]">Message for {data.student_name}'s parents</h2>
        <div className="flex flex-wrap gap-2 mt-3">
          <Pill variant="brand">Grade {data.grade}</Pill>
          <Pill variant="outline">{data.channel}</Pill>
          <Pill variant="neutral">{data.tone}</Pill>
        </div>
      </PremiumCard>

      <PremiumCard className="p-6" hover={false}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-base font-semibold text-[#111111]">Subject line</h3>
          <button
            onClick={() => doCopy(data.subject_line, "subject")}
            className="text-xs text-[#A855F7] hover:underline flex items-center gap-1"
          >
            {copied === "subject" ? <><Check className="w-3 h-3"/> Copied</> : <><Copy className="w-3 h-3"/> Copy</>}
          </button>
        </div>
        <p className="text-sm font-medium text-slate-700 bg-slate-50 rounded-lg p-3">{data.subject_line}</p>
      </PremiumCard>

      <PremiumCard className="p-6" hover={false}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-base font-semibold text-[#111111]">Full message</h3>
          <button
            onClick={() => doCopy(fullMessage, "full")}
            className="text-xs text-[#A855F7] hover:underline flex items-center gap-1"
          >
            {copied === "full" ? <><Check className="w-3 h-3"/> Copied</> : <><Copy className="w-3 h-3"/> Copy</>}
          </button>
        </div>
        <div className="space-y-3 text-sm text-slate-700 whitespace-pre-wrap">
          <p>{data.opening}</p>
          <p>{data.body}</p>
          <p className="font-medium text-[#A855F7]">{data.call_to_action}</p>
          <p className="italic">{data.closing}</p>
        </div>
      </PremiumCard>

      {data.whatsapp_short && (
        <PremiumCard className="p-6" hover={false}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading text-base font-semibold text-[#111111]">WhatsApp version</h3>
            <button
              onClick={() => doCopy(data.whatsapp_short, "wa")}
              className="text-xs text-emerald-600 hover:underline flex items-center gap-1"
            >
              {copied === "wa" ? <><Check className="w-3 h-3"/> Copied</> : <><Copy className="w-3 h-3"/> Copy for WhatsApp</>}
            </button>
          </div>
          <p className="text-sm text-slate-700 bg-emerald-50 rounded-lg p-3 whitespace-pre-wrap">{data.whatsapp_short}</p>
        </PremiumCard>
      )}

      {data.hindi_translation && (
        <PremiumCard className="p-6" hover={false}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading text-base font-semibold text-[#111111]">हिन्दी संस्करण</h3>
            <button
              onClick={() => doCopy(data.hindi_translation, "hi")}
              className="text-xs text-[#A855F7] hover:underline flex items-center gap-1"
            >
              {copied === "hi" ? <><Check className="w-3 h-3"/> Copied</> : <><Copy className="w-3 h-3"/> Copy</>}
            </button>
          </div>
          <p className="text-sm text-slate-700 whitespace-pre-wrap">{data.hindi_translation}</p>
        </PremiumCard>
      )}

      {data.follow_up_suggestion && (
        <PremiumCard className="p-5" hover={false}>
          <p className="text-xs uppercase tracking-widest text-slate-500 mb-1">Follow-up</p>
          <p className="text-sm text-slate-700">{data.follow_up_suggestion}</p>
        </PremiumCard>
      )}

      {/* Voice-note — parents get an audio message they can play on WhatsApp */}
      <PremiumCard className="p-6" hover={false}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-heading text-base font-semibold text-[#111111] flex items-center gap-2">
              <Volume2 className="w-4 h-4 text-[#A855F7]" /> Voice note for WhatsApp
              <span className="text-[9px] font-bold bg-gradient-to-r from-orange-500 to-pink-500 text-white px-1.5 py-0.5 rounded uppercase tracking-wider ml-1">NEW</span>
            </h3>
            <p className="text-xs text-slate-500 mt-1">Generate a natural-sounding Indian-voice audio note (2-min max) — parents can play it directly on WhatsApp.</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select value={voiceLang} onChange={(e) => setVoiceLang(e.target.value)} className="text-xs border border-slate-300 rounded-md px-2 py-1.5 bg-white" data-testid="parent-voice-lang">
            <option value="hi-IN">Hindi</option>
            <option value="en-IN">English (India)</option>
            <option value="ta-IN">Tamil</option>
            <option value="te-IN">Telugu</option>
            <option value="bn-IN">Bengali</option>
            <option value="mr-IN">Marathi</option>
            <option value="gu-IN">Gujarati</option>
            <option value="kn-IN">Kannada</option>
            <option value="ml-IN">Malayalam</option>
            <option value="pa-IN">Punjabi</option>
            <option value="od-IN">Odia</option>
          </select>
          <select value={voiceSpeaker} onChange={(e) => setVoiceSpeaker(e.target.value)} className="text-xs border border-slate-300 rounded-md px-2 py-1.5 bg-white capitalize" data-testid="parent-voice-speaker">
            <option value="shreya">Shreya (Female)</option>
            <option value="priya">Priya (Female)</option>
            <option value="kavitha">Kavitha (Female)</option>
            <option value="simran">Simran (Female)</option>
            <option value="aditya">Aditya (Male)</option>
            <option value="rahul">Rahul (Male)</option>
            <option value="kabir">Kabir (Male)</option>
            <option value="vijay">Vijay (Male)</option>
          </select>
          <button
            onClick={generateVoiceNote}
            disabled={voiceLoading}
            className="text-xs font-semibold px-3 py-1.5 bg-[#111111] hover:bg-[#333] text-white rounded-md flex items-center gap-1.5 disabled:opacity-50"
            data-testid="parent-voice-generate"
          >
            {voiceLoading ? <><Loader2 className="w-3 h-3 animate-spin"/> Generating…</> : <><Play className="w-3 h-3"/> {voiceUrl ? "Re-generate" : "Generate voice note"}</>}
          </button>
        </div>
        {voiceUrl && (
          <div className="mt-4 flex items-center gap-3 bg-gradient-to-br from-emerald-50 to-emerald-100/50 border border-emerald-200 rounded-lg p-3">
            <audio controls src={voiceUrl} className="flex-1" data-testid="parent-voice-audio" />
            <a href={voiceUrl} download={`parent-note-${data.student_name || "student"}.wav`} className="text-xs font-semibold text-emerald-700 hover:underline flex items-center gap-1" data-testid="parent-voice-download">
              <Download className="w-3 h-3" /> Save
            </a>
          </div>
        )}
      </PremiumCard>
    </div>
  );
}

// ─── Course Outline renderer ──────────────────────────────────────────
function CourseOutlineResult({ data }) {
  return (
    <div className="space-y-6" data-testid="course-outline-result">
      <PremiumCard className="p-6" hover={false}>
        <h2 className="font-heading text-2xl font-bold text-[#111111]">{data.course_title}</h2>
        <div className="flex flex-wrap gap-2 mt-3">
          <Pill variant="brand">Grade {data.grade}</Pill>
          {data.subject && <Pill variant="outline">{data.subject}</Pill>}
          <Pill variant="neutral">{data.total_weeks} weeks · {data.hours_per_week} hrs/wk</Pill>
          <Pill variant="neutral">{data.total_hours} total hours</Pill>
        </div>
        {data.overview && <p className="text-sm text-slate-600 mt-4">{data.overview}</p>}
      </PremiumCard>

      {data.learning_outcomes?.length > 0 && (
        <PremiumCard className="p-6" hover={false}>
          <h3 className="font-heading text-lg font-semibold text-[#111111] mb-3">Learning outcomes</h3>
          <ul className="space-y-2">
            {data.learning_outcomes.map((o, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                <Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0"/>{o}
              </li>
            ))}
          </ul>
        </PremiumCard>
      )}

      {data.prerequisites?.length > 0 && (
        <PremiumCard className="p-6" hover={false}>
          <h3 className="font-heading text-base font-semibold text-[#111111] mb-3">Prerequisites</h3>
          <ul className="text-sm text-slate-600 space-y-1">
            {data.prerequisites.map((p, i) => <li key={i}>· {p}</li>)}
          </ul>
        </PremiumCard>
      )}

      <div className="space-y-4">
        <h3 className="font-heading text-lg font-semibold text-[#111111]">Weekly modules</h3>
        {(data.modules || []).map((m) => (
          <PremiumCard key={m.week} className="p-5" hover={false}>
            <div className="flex items-baseline justify-between gap-4 mb-2">
              <div>
                <p className="text-xs uppercase tracking-widest text-[#A855F7] font-semibold">Week {m.week}</p>
                <h4 className="font-heading text-lg font-bold text-[#111111]">{m.title}</h4>
              </div>
              {m.focus && <p className="text-xs text-slate-500 italic max-w-sm text-right">{m.focus}</p>}
            </div>
            <div className="grid md:grid-cols-2 gap-4 mt-3">
              {m.topics?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Topics</p>
                  <ul className="text-sm text-slate-700 space-y-0.5">
                    {m.topics.map((t, i) => <li key={i}>· {t}</li>)}
                  </ul>
                </div>
              )}
              {m.activities?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Activities</p>
                  <ul className="text-sm text-slate-700 space-y-0.5">
                    {m.activities.map((a, i) => <li key={i}>· {a}</li>)}
                  </ul>
                </div>
              )}
            </div>
            {m.assessment && (
              <p className="text-xs text-slate-600 mt-3 pt-3 border-t border-slate-100">
                <span className="font-semibold text-slate-500">Assessment:</span> {m.assessment}
              </p>
            )}
          </PremiumCard>
        ))}
      </div>

      {data.milestones?.length > 0 && (
        <PremiumCard className="p-6" hover={false}>
          <h3 className="font-heading text-lg font-semibold text-[#111111] mb-3">Milestones</h3>
          <div className="space-y-2">
            {data.milestones.map((m, i) => (
              <div key={i} className="flex items-start gap-3 text-sm">
                <Pill variant="brand" className="shrink-0">Week {m.week}</Pill>
                <div>
                  <p className="font-medium text-[#111111]">{m.title} <span className="text-xs text-slate-500">({m.type})</span></p>
                  {m.description && <p className="text-slate-600">{m.description}</p>}
                </div>
              </div>
            ))}
          </div>
        </PremiumCard>
      )}

      {data.final_assessment && (
        <PremiumCard className="p-6" hover={false}>
          <h3 className="font-heading text-base font-semibold text-[#111111] mb-2">Final assessment</h3>
          <p className="text-xs text-slate-500 uppercase tracking-widest">{data.final_assessment.type}</p>
          <p className="text-sm text-slate-700 mt-2">{data.final_assessment.description}</p>
        </PremiumCard>
      )}

      {data.teacher_tips?.length > 0 && (
        <PremiumCard className="p-6 bg-gradient-to-br from-amber-50 to-white border-amber-200" hover={false}>
          <h3 className="font-heading text-base font-semibold text-[#111111] mb-3">Teacher tips</h3>
          <ul className="space-y-2 text-sm text-slate-700">
            {data.teacher_tips.map((t, i) => <li key={i} className="flex items-start gap-2"><Sparkles className="w-4 h-4 text-amber-500 mt-0.5 shrink-0"/>{t}</li>)}
          </ul>
        </PremiumCard>
      )}
    </div>
  );
}

// Generic doc renderer — handles teaching-notes + study-material (similar shapes)
function DocumentResult({ data }) {
  const sections = data.sections || data.key_concepts || [];
  return (
    <div className="space-y-6">
      <PremiumCard className="p-6" hover={false}>
        <h2 className="font-heading text-2xl font-bold text-[#111111]">{data.title}</h2>
        <div className="flex flex-wrap gap-2 mt-3">
          {data.grade && <Pill variant="brand">Grade {data.grade}</Pill>}
          {data.subject && <Pill variant="outline">{data.subject}</Pill>}
        </div>
        {(data.introduction || data.overview) && (
          <p className="mt-4 text-sm text-slate-700 leading-relaxed">{data.introduction || data.overview}</p>
        )}
      </PremiumCard>

      {(data.learning_objectives || []).length > 0 && (
        <PremiumCard className="p-6" hover={false}>
          <h3 className="font-heading text-lg font-semibold text-[#111111] mb-3">Learning objectives</h3>
          <ul className="space-y-2">
            {data.learning_objectives.map((o, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-700"><Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0"/>{o}</li>
            ))}
          </ul>
        </PremiumCard>
      )}

      <PremiumCard className="p-6" hover={false}>
        <h3 className="font-heading text-lg font-semibold text-[#111111] mb-4">{data.sections ? "Sections" : "Key concepts"}</h3>
        <div className="space-y-4">
          {sections.map((s, i) => (
            <div key={i} className="p-4 rounded-xl border border-slate-200 bg-slate-50/40">
              <h4 className="font-semibold text-[#111111]">{s.heading || s.concept}</h4>
              {s.content && <p className="mt-2 text-sm text-slate-700">{s.content}</p>}
              {s.explanation && <p className="mt-2 text-sm text-slate-700">{s.explanation}</p>}
              {s.example && <p className="mt-2 text-xs text-[#A855F7] font-medium">Example: {s.example}</p>}
              {(s.key_terms || []).length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {s.key_terms.map((t, j) => (
                    <span key={j} className="px-2 py-1 bg-white border border-slate-200 rounded-md text-xs"><b>{t.term}</b> — {t.meaning || t.definition}</span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </PremiumCard>

      {(data.common_misconceptions || []).length > 0 && (
        <PremiumCard className="p-6" hover={false}>
          <h3 className="font-heading text-base font-semibold text-[#111111] mb-3">Common misconceptions</h3>
          <ul className="space-y-1.5 text-sm text-slate-700">
            {data.common_misconceptions.map((m, i) => <li key={i}>· {m}</li>)}
          </ul>
        </PremiumCard>
      )}

      {(data.teaching_tips || []).length > 0 && (
        <PremiumCard className="p-6 bg-brand-50/40" hover={false}>
          <h3 className="font-heading text-base font-semibold text-[#111111] mb-3">Teaching tips</h3>
          <ul className="space-y-1.5 text-sm text-slate-700">
            {data.teaching_tips.map((t, i) => <li key={i}>· {t}</li>)}
          </ul>
        </PremiumCard>
      )}

      {(data.discussion_questions || data.self_check_questions || []).length > 0 && (
        <PremiumCard className="p-6" hover={false}>
          <h3 className="font-heading text-base font-semibold text-[#111111] mb-3">{data.discussion_questions ? "Discussion questions" : "Self-check"}</h3>
          <ol className="list-decimal list-inside space-y-1.5 text-sm text-slate-700">
            {(data.discussion_questions || data.self_check_questions || []).map((q, i) => <li key={i}>{q}</li>)}
          </ol>
        </PremiumCard>
      )}

      {(data.summary_points || []).length > 0 && (
        <PremiumCard className="p-6" hover={false}>
          <h3 className="font-heading text-base font-semibold text-[#111111] mb-3">Summary points</h3>
          <ul className="space-y-1.5 text-sm text-slate-700">
            {data.summary_points.map((s, i) => <li key={i}>· {s}</li>)}
          </ul>
        </PremiumCard>
      )}
    </div>
  );
}

function QuestionBankResult({ data }) {
  return (
    <div className="space-y-6">
      <PremiumCard className="p-6" hover={false}>
        <h2 className="font-heading text-2xl font-bold text-[#111111]">{data.topic} — Question Bank</h2>
        <div className="flex flex-wrap gap-2 mt-3">
          <Pill variant="brand">Grade {data.grade}</Pill>
          {data.subject && <Pill variant="outline">{data.subject}</Pill>}
          <Pill variant="dark">{data.total} questions</Pill>
        </div>
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <div className="p-3 rounded-lg bg-emerald-50 text-emerald-800"><b>Easy:</b> {data.by_difficulty?.easy || 0}</div>
          <div className="p-3 rounded-lg bg-amber-50 text-amber-800"><b>Medium:</b> {data.by_difficulty?.medium || 0}</div>
          <div className="p-3 rounded-lg bg-rose-50 text-rose-800"><b>Hard:</b> {data.by_difficulty?.hard || 0}</div>
          <div className="p-3 rounded-lg bg-brand-50 text-[#7E22CE]"><b>MCQ:</b> {data.by_type?.mcq || 0}</div>
        </div>
      </PremiumCard>
      <PremiumCard className="p-6" hover={false}>
        <div className="space-y-3">
          {(data.questions || []).map((q, i) => (
            <div key={i} className="p-4 rounded-xl border border-slate-200">
              <div className="flex items-start justify-between flex-wrap gap-2 mb-2">
                <div className="text-xs font-semibold text-slate-500">Q{q.id || i + 1} · {q.type?.toUpperCase()} · {q.difficulty}</div>
                <Pill variant="brand">{q.marks || 1} marks</Pill>
              </div>
              <p className="text-sm font-medium text-[#111111]">{q.question}</p>
              {(q.options || []).length > 0 && (
                <ul className="mt-2 space-y-1 text-sm text-slate-600">{q.options.map((o, j) => <li key={j}>{o}</li>)}</ul>
              )}
              <p className="mt-2 text-xs text-emerald-700"><b>Answer:</b> {q.answer}</p>
              {q.topic_tag && <p className="mt-1 text-xs text-slate-400">Tag: {q.topic_tag}</p>}
            </div>
          ))}
        </div>
      </PremiumCard>
    </div>
  );
}

function RubricResult({ data }) {
  return (
    <div className="space-y-6">
      <PremiumCard className="p-6" hover={false}>
        <h2 className="font-heading text-2xl font-bold text-[#111111]">Rubric — {data.assignment}</h2>
        <div className="flex flex-wrap gap-2 mt-3">
          <Pill variant="brand">Grade {data.grade}</Pill>
          <Pill variant="dark">{data.total_points || 100} points</Pill>
        </div>
        <p className="mt-4 text-sm text-slate-600">{data.scoring_guide}</p>
      </PremiumCard>

      <PremiumCard className="p-6 overflow-x-auto" hover={false}>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200">
              <th className="text-left py-2 pr-3 font-semibold text-[#111111]">Criterion</th>
              <th className="text-left py-2 px-3 font-semibold text-[#111111] w-16">Weight</th>
              {(data.levels || []).map((l) => (
                <th key={l.name} className="text-left py-2 px-3 font-semibold text-[#111111] min-w-[160px]">{l.name}<div className="text-xs font-normal text-slate-400">{l.range}</div></th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(data.criteria || []).map((c, i) => (
              <tr key={i} className="border-b border-slate-100 last:border-0 align-top">
                <td className="py-3 pr-3 font-medium text-[#111111]">{c.name}</td>
                <td className="py-3 px-3 text-slate-600">{c.weight_pct}%</td>
                <td className="py-3 px-3 text-xs text-slate-700">{c.descriptions?.excellent}</td>
                <td className="py-3 px-3 text-xs text-slate-700">{c.descriptions?.proficient}</td>
                <td className="py-3 px-3 text-xs text-slate-700">{c.descriptions?.developing}</td>
                <td className="py-3 px-3 text-xs text-slate-700">{c.descriptions?.beginning}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </PremiumCard>
    </div>
  );
}

function HomeworkResult({ data }) {
  return (
    <div className="space-y-6">
      <PremiumCard className="p-6" hover={false}>
        <h2 className="font-heading text-2xl font-bold text-[#111111]">{data.topic} — Homework</h2>
        <div className="flex flex-wrap gap-2 mt-3">
          <Pill variant="brand">Grade {data.grade}</Pill>
          {data.subject && <Pill variant="outline">{data.subject}</Pill>}
          <Pill variant="dark">{data.total_days} days · {data.minutes_per_day} min/day</Pill>
        </div>
        {data.weekly_goal && <p className="mt-4 text-sm text-slate-700"><b>Weekly goal:</b> {data.weekly_goal}</p>}
      </PremiumCard>

      <div className="space-y-3">
        {(data.days || []).map((d, i) => (
          <PremiumCard key={i} className="p-5" hover={false}>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h3 className="font-heading text-base font-semibold text-[#111111]">Day {d.day} — {d.focus}</h3>
              <Pill variant="neutral">{(d.tasks || []).reduce((acc, t) => acc + (t.time_min || 0), 0)} min</Pill>
            </div>
            <ul className="mt-3 space-y-2">
              {(d.tasks || []).map((t, j) => (
                <li key={j} className="flex items-start gap-2 text-sm">
                  <Pill variant="brand">{t.type}</Pill>
                  <span className="text-slate-700 flex-1">{t.task}</span>
                  <span className="text-xs text-slate-400">{t.time_min} min</span>
                </li>
              ))}
            </ul>
            {d.parent_note && <p className="mt-3 text-xs text-slate-500 italic">For parents: {d.parent_note}</p>}
          </PremiumCard>
        ))}
      </div>
    </div>
  );
}

function AssignmentResult({ data }) {
  return (
    <div className="space-y-6">
      <PremiumCard className="p-6" hover={false}>
        <h2 className="font-heading text-2xl font-bold text-[#111111]">{data.title}</h2>
        <div className="flex flex-wrap gap-2 mt-3">
          <Pill variant="brand">Grade {data.grade}</Pill>
          {data.subject && <Pill variant="outline">{data.subject}</Pill>}
          <Pill variant="dark">{data.type}</Pill>
          <Pill variant="neutral">{data.duration_days} days</Pill>
        </div>
        {data.objective && <p className="mt-4 text-sm text-slate-700"><b>Objective:</b> {data.objective}</p>}
        {data.description && <p className="mt-3 text-sm text-slate-600 leading-relaxed">{data.description}</p>}
      </PremiumCard>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <PremiumCard className="p-6" hover={false}>
          <h3 className="font-heading text-base font-semibold text-[#111111] mb-3">Deliverables</h3>
          <ul className="space-y-1.5 text-sm text-slate-700">
            {(data.deliverables || []).map((d, i) => <li key={i}>· {d}</li>)}
          </ul>
        </PremiumCard>
        <PremiumCard className="p-6" hover={false}>
          <h3 className="font-heading text-base font-semibold text-[#111111] mb-3">Resources</h3>
          <ul className="space-y-1.5 text-sm text-slate-700">
            {(data.resources || []).map((r, i) => <li key={i}>· {r}</li>)}
          </ul>
        </PremiumCard>
      </div>

      <PremiumCard className="p-6" hover={false}>
        <h3 className="font-heading text-base font-semibold text-[#111111] mb-3">Milestones</h3>
        <div className="space-y-2">
          {(data.milestones || []).map((m, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-lg border border-slate-200">
              <Pill variant="brand">Day {m.day}</Pill>
              <span className="text-sm text-slate-700 flex-1">{m.goal}</span>
            </div>
          ))}
        </div>
      </PremiumCard>

      <PremiumCard className="p-6" hover={false}>
        <h3 className="font-heading text-base font-semibold text-[#111111] mb-3">Evaluation criteria</h3>
        <div className="space-y-2">
          {(data.evaluation_criteria || []).map((c, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="flex-1 text-sm text-slate-700">{c.criterion}</div>
              <Pill variant="brand">{c.weight_pct}%</Pill>
            </div>
          ))}
        </div>
        {data.submission_format && <p className="mt-4 pt-4 border-t border-slate-100 text-sm text-slate-700"><b>Submission:</b> {data.submission_format}</p>}
      </PremiumCard>
    </div>
  );
}

export default function TeachingTool() {
  const { tool } = useParams();
  const navigate = useNavigate();
  const cfg = TOOL_CONFIG[tool];

  const [form, setForm] = useState(() =>
    cfg ? Object.fromEntries(cfg.fields.map((f) => [f.name, f.default ?? ""])) : {}
  );
  const [language, setLanguage] = useState("english");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  if (!cfg) {
    return (
      <div className="min-h-screen bg-white font-sans">
        <PremiumNav />
        <div className="max-w-3xl mx-auto px-5 py-24 text-center">
          <h1 className="font-heading text-3xl font-bold text-[#111111]">Tool not found</h1>
          <p className="mt-3 text-slate-600">Pick a teaching tool from the Teaching Suite.</p>
          <PremiumButton className="mt-6" icon={ArrowLeft} onClick={() => navigate("/teaching")}>Back to Teaching Suite</PremiumButton>
        </div>
        <PremiumFooter />
      </div>
    );
  }

  const run = async () => {
    // Dynamic required-field check — supports every tool config, not just those with a `topic` field
    const requiredFields = (cfg.fields || []).filter((f) => f.required);
    for (const f of requiredFields) {
      if (!form[f.name]?.toString().trim()) {
        setError(`${f.label} is required.`);
        return;
      }
    }
    setLoading(true); setError(""); setResult(null);
    try {
      const r = await axios.post(`${API}${cfg.endpoint}`, { ...form, language }, { withCredentials: true });
      setResult(r.data?.result || r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Generation failed");
    } finally { setLoading(false); }
  };

  const copyJson = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(result, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_e) { /* clipboard unavailable */ }
  };

  const printPage = () => window.print();

  const Renderer = RENDERERS[tool];

  return (
    <div className="min-h-screen bg-white font-sans" data-testid={`teaching-tool-${tool}`}>
      <PremiumNav />

      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10">
        <button
          onClick={() => navigate("/teaching")}
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#A855F7] mb-6 transition-colors"
          data-testid="teaching-back"
        >
          <ArrowLeft className="w-4 h-4"/> Back to Teaching Suite
        </button>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <SectionHeading
            eyebrow={<><cfg.Icon className="w-3 h-3 inline -mt-0.5 mr-1"/> {cfg.eyebrow}</>}
            title={cfg.title}
            subtitle={cfg.subtitle}
          />
        </motion.div>

        {/* Form */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }} className="mt-8">
          <PremiumCard className="p-6" hover={false}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {cfg.fields.map((f) => (
                <div key={f.name} className={f.name === "topic" ? "sm:col-span-2 lg:col-span-3" : ""}>
                  {f.type !== "boolean" && (
                    <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">{f.label}</label>
                  )}
                  <FieldInput field={f} value={form[f.name]} onChange={(v) => setForm({ ...form, [f.name]: v })}/>
                </div>
              ))}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Output language</label>
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  data-testid="teaching-language"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-[#A855F7] focus:border-transparent"
                >
                  <option value="english">English</option>
                  <option value="hindi">हिंदी (Hindi)</option>
                  <option value="hinglish">Hinglish</option>
                </select>
              </div>
            </div>
            <div className="mt-5 flex items-center justify-between flex-wrap gap-3">
              <p className="text-xs text-slate-500">Powered by Claude Haiku 4.5 · ~5-15s</p>
              <PremiumButton onClick={run} loading={loading} icon={Sparkles} size="lg" data-testid="teaching-run">
                {loading ? "Generating…" : `Generate ${cfg.title.replace(/^AI /, "")}`}
              </PremiumButton>
            </div>
            {error && (
              <div className="mt-4 text-sm text-red-600 flex items-center gap-2" data-testid="teaching-error">
                <AlertCircle className="w-4 h-4"/> {error}
              </div>
            )}
          </PremiumCard>
        </motion.div>

        {loading && (
          <div className="mt-10 flex flex-col items-center justify-center text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin text-[#A855F7]"/>
            <div className="mt-3 text-sm">Generating with Claude Haiku 4.5…</div>
          </div>
        )}

        {result && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mt-10" data-testid="teaching-result">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
              <h2 className="font-heading text-xl font-semibold text-[#111111]">Result</h2>
              <div className="flex items-center gap-2">
                <PremiumButton variant="secondary" size="sm" icon={copied ? Check : Copy} onClick={copyJson}>
                  {copied ? "Copied" : "Copy JSON"}
                </PremiumButton>
                <PremiumButton variant="secondary" size="sm" icon={Download} onClick={printPage}>
                  Print / PDF
                </PremiumButton>
              </div>
            </div>
            <Renderer data={result} />
          </motion.div>
        )}
      </div>

      <PremiumFooter />
    </div>
  );
}
